import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";
import api from "@/lib/api";
import { Search, ArrowRight, Clock, Calendar, Newspaper } from "lucide-react";

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/public/blog/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    const c = params.get("category");
    if (c) setCategory(c);
    const s = params.get("q");
    if (s) setQ(s);
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category !== "all") p.set("category", category);
    if (q) p.set("q", q);
    p.set("page", page);
    api.get(`/public/blog?${p.toString()}`)
      .then((r) => { setPosts(r.data.posts || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, q, page]);

  const totalPages = Math.max(1, Math.ceil(total / 12));
  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const p = new URLSearchParams(params);
    q ? p.set("q", q) : p.delete("q");
    setParams(p);
  };

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]" data-testid="blog-list-page">
      <SEO
        title="Travel Guide & Visa News — SchengenFile Blog"
        description="Guides, checklists, and Schengen visa news. Practical, up-to-date advice from SchengenFile's document specialists."
        jsonLd={{
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          ],
        }}
      />
      <Header />
      <main>
        <section className="py-14 sm:py-20 bg-[#0f1b2d] text-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="text-xs uppercase tracking-widest text-[#60a5fa] font-semibold">Blog</div>
            <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl">Travel Guide & Visa News</h1>
            <p className="mt-3 text-white/70 max-w-2xl">
              Practical guides, checklists, and up-to-date visa news from our document specialists.
            </p>
            <form onSubmit={onSearch} className="mt-6 flex gap-2 max-w-lg" data-testid="blog-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search articles"
                  data-testid="blog-search-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                />
              </div>
              <button type="submit" className="px-4 py-2.5 rounded-md bg-white text-[#0f1b2d] font-semibold text-sm" data-testid="blog-search-submit">Search</button>
            </form>
          </div>
        </section>

        <section className="py-8 border-b border-[#e2e8f0]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 flex flex-wrap gap-2" data-testid="blog-category-tabs">
            {[{ id: "all", label: "All" }, ...categories].map((c) => (
              <button
                key={c.id}
                onClick={() => { setCategory(c.id); setPage(1); }}
                data-testid={`blog-category-${c.id}`}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-full border transition ${
                  category === c.id
                    ? "bg-[#0f1b2d] text-white border-[#0f1b2d]"
                    : "bg-white text-[#475569] border-[#e2e8f0] hover:border-[#0f1b2d]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-72 rounded-xl bg-[#f8fafc] animate-pulse" />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 text-[#64748b]">
                <Newspaper className="w-10 h-10 mx-auto text-[#cbd5e1]" />
                <div className="mt-3 font-semibold text-[#0f1b2d]">No articles found</div>
                <div className="text-sm">Try a different category or search term.</div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
                  {posts.map((p) => <PostCard key={p.id} post={p} />)}
                </div>
                {totalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2" data-testid="blog-pagination">
                    <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded border border-[#e2e8f0] text-sm disabled:opacity-40">Previous</button>
                    <span className="text-sm text-[#475569] px-3">Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded border border-[#e2e8f0] text-sm disabled:opacity-40">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PostCard({ post }) {
  const cat = post.category || "general";
  const isNews = post.is_news;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group block rounded-xl border border-[#e2e8f0] bg-white overflow-hidden hover:border-[#2563eb] hover:-translate-y-0.5 transition-all"
    >
      <div className="aspect-[16/9] bg-gradient-to-br from-[#eff6ff] to-[#dbeafe] relative">
        {post.featured_image ? (
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="font-display font-black text-3xl text-[#2563eb]/40">SchengenFile</div>
          </div>
        )}
        <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
          isNews ? "bg-[#dc2626] text-white" : "bg-white text-[#0f1b2d]"
        }`}>
          {isNews ? "News" : (categoryLabel(cat))}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg text-[#0f1b2d] leading-snug group-hover:text-[#2563eb] transition">
          {post.title}
        </h3>
        {post.excerpt && <p className="mt-2 text-sm text-[#475569] line-clamp-2">{post.excerpt}</p>}
        <div className="mt-3 flex items-center gap-3 text-xs text-[#64748b]">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{post.reading_time || 5} min</span>
          {post.author?.name && <span className="text-[#94a3b8]">· {post.author.name}</span>}
        </div>
      </div>
    </Link>
  );
}

function categoryLabel(id) {
  return ({ country_guides: "Country Guides", document_tips: "Document Tips", visa_news: "Visa News", application_process: "Application" })[id] || id;
}
function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
}
